/**
 * Utility class for IP address validation
 */
export class IpValidationUtil {
    /**
     * Validates an IP address string, supporting both regular IPv4 and CIDR notation
     * @param ip The IP address string to validate
     * @returns true if the IP address is valid, false otherwise
     */
    public static validateIpAddress(ip: string): boolean {
        if (!ip || ip.trim() === '') {
            return true; // Empty IPs are considered valid (will be filtered out)
        }

        const trimmedIp = ip.trim();

        // Check for CIDR notation (IP/prefix)
        if (trimmedIp.includes('/')) {
            const parts = trimmedIp.split('/');
            if (parts.length !== 2) {
                return false;
            }

            const ipPart = parts[0];
            const prefixPart = parts[1];

            // Validate IP part
            if (!this.isValidIpv4(ipPart)) {
                return false;
            }

            // Validate prefix (0-32) - ensure it's a valid number
            if (!/^\d+$/.test(prefixPart)) {
                return false;
            }

            const prefix = parseInt(prefixPart, 10);
            if (isNaN(prefix) || prefix < 0 || prefix > 32) {
                return false;
            }

            return true;
        }

        // Validate as regular IPv4
        return this.isValidIpv4(trimmedIp);
    }

    /**
     * Validates if a string is a valid IPv4 address
     * @param ip The IP address string to validate
     * @returns true if the string is a valid IPv4 address, false otherwise
     */
    public static isValidIpv4(ip: string): boolean {
        const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        return ipv4Regex.test(ip);
    }

    /**
     * Checks if a given IP address is covered by any of the allowed IP addresses/ranges
     * @param targetIp The IP address to check
     * @param allowedIPs Array of allowed IP addresses and CIDR ranges
     * @returns true if the target IP is covered by any of the allowed IPs, false otherwise
     */
    public static isIpCovered(targetIp: string, allowedIPs: string[]): boolean {
        if (!targetIp || !this.isValidIpv4(targetIp)) {
            return false;
        }

        for (const allowedIp of allowedIPs) {
            if (!allowedIp || allowedIp.trim() === '') {
                continue;
            }

            const trimmedAllowedIp = allowedIp.trim();

            // Check for CIDR notation
            if (trimmedAllowedIp.includes('/')) {
                const parts = trimmedAllowedIp.split('/');
                if (parts.length === 2) {
                    const networkIp = parts[0];
                    const prefix = parseInt(parts[1], 10);

                    if (this.isValidIpv4(networkIp) && !isNaN(prefix) && prefix >= 0 && prefix <= 32) {
                        if (this.isIpInCidrRange(targetIp, networkIp, prefix)) {
                            return true;
                        }
                    }
                }
            } else {
                // Check for exact IP match
                if (this.isValidIpv4(trimmedAllowedIp) && targetIp === trimmedAllowedIp) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Checks if an IP address is within a CIDR range
     * @param targetIp The IP address to check
     * @param networkIp The network IP address
     * @param prefix The CIDR prefix length
     * @returns true if the target IP is within the CIDR range, false otherwise
     */
    private static isIpInCidrRange(targetIp: string, networkIp: string, prefix: number): boolean {
        const targetParts = targetIp.split('.').map(part => parseInt(part, 10));
        const networkParts = networkIp.split('.').map(part => parseInt(part, 10));

        // Calculate the network mask
        const mask = (0xFFFFFFFF << (32 - prefix)) >>> 0;
        const maskParts = [
            (mask >>> 24) & 0xFF,
            (mask >>> 16) & 0xFF,
            (mask >>> 8) & 0xFF,
            mask & 0xFF
        ];

        // Apply mask to both IPs
        const targetNetwork = targetParts.map((part, i) => part & maskParts[i]);
        const networkNetwork = networkParts.map((part, i) => part & maskParts[i]);

        // Compare the network portions
        return targetNetwork.every((part, i) => part === networkNetwork[i]);
    }
}
